import { useRef } from 'react';
import { CircleMarker, useMapEvents } from 'react-leaflet';
import type { RiverLinePath, RiverLineCoordinate, RiverLineSelection } from './river-geojson';

function roundCoordinate(value: number) {
  return Math.round(value * 1000000) / 1000000;
}

function getCoordinateKey([lng, lat]: RiverLineCoordinate) {
  return `${lng}:${lat}`;
}

interface RiverLineDrawingLayerProps {
  paths: RiverLinePath[];
  drawingEnabled: boolean;
  activeSelection: RiverLineSelection | null;
  onAddCoordinate: (coordinate: RiverLineCoordinate) => void;
  onSelectCoordinate: (selection: RiverLineSelection) => void;
}

interface CoordinateMarkerEntry {
  coordinate: RiverLineCoordinate;
  selections: RiverLineSelection[];
}

export function RiverLineDrawingLayer({
  paths,
  drawingEnabled,
  activeSelection,
  onAddCoordinate,
  onSelectCoordinate,
}: RiverLineDrawingLayerProps) {
  const suppressNextMapClickRef = useRef(false);
  const drawingEnabledRef = useRef(drawingEnabled);
  const onAddCoordinateRef = useRef(onAddCoordinate);
  const onSelectCoordinateRef = useRef(onSelectCoordinate);
  const coordinateMap = new Map<string, CoordinateMarkerEntry>();

  drawingEnabledRef.current = drawingEnabled;
  onAddCoordinateRef.current = onAddCoordinate;
  onSelectCoordinateRef.current = onSelectCoordinate;

  paths.forEach((path, branchIndex) => {
    path.forEach((coordinate, coordinateIndex) => {
      const key = getCoordinateKey(coordinate);
      const existing = coordinateMap.get(key);
      const selection = { branchIndex, coordinateIndex };

      if (existing) {
        existing.selections.push(selection);
        return;
      }

      coordinateMap.set(key, {
        coordinate,
        selections: [selection],
      });
    });
  });

  const markers = Array.from(coordinateMap.values());

  useMapEvents({
    click: (event) => {
      if (suppressNextMapClickRef.current) {
        suppressNextMapClickRef.current = false;
        return;
      }

      if (!drawingEnabledRef.current) {
        return;
      }

      onAddCoordinateRef.current([roundCoordinate(event.latlng.lng), roundCoordinate(event.latlng.lat)]);
    },
  });

  return (
    <>
      {markers.map(({ coordinate, selections }) => {
        const [lng, lat] = coordinate;
        const selected = selections.some((selection) => {
          return (
            activeSelection?.branchIndex === selection.branchIndex &&
            activeSelection.coordinateIndex === selection.coordinateIndex
          );
        });
        const branchPoint = selections.length > 1;
        const selectTarget =
          selections.find((selection) => {
            return (
              activeSelection?.branchIndex === selection.branchIndex &&
              activeSelection.coordinateIndex === selection.coordinateIndex
            );
          }) || selections[selections.length - 1];
        const radius = selected ? 11 : branchPoint ? 8.5 : 6.5;
        const strokeColor = selected ? '#9d174d' : branchPoint ? '#b45309' : '#1d4ed8';
        const fillColor = selected ? '#f9a8d4' : branchPoint ? '#fbbf24' : '#93c5fd';
        const weight = selected ? 4.5 : branchPoint ? 3.5 : 3;

        return (
          <CircleMarker
            key={getCoordinateKey(coordinate)}
            center={[lat, lng]}
            radius={radius}
            eventHandlers={{
              click: (event) => {
                suppressNextMapClickRef.current = true;
                event.originalEvent.stopPropagation();
                onSelectCoordinateRef.current(selectTarget);
              },
            }}
            pathOptions={{
              color: strokeColor,
              fillColor,
              fillOpacity: selected ? 1 : branchPoint ? 0.98 : 0.94,
              weight,
            }}
          />
        );
      })}
    </>
  );
}
